/**
 * PatientAppointmentRowActions — per-row kebab for the patient appointments
 * table. Ports Flutter `patient_appointments_tab.dart` `menuOptions()` (:307).
 *
 * Menu shape (Flutter):
 *   Cancelled / NoShow rows → ONLY "View Appointment".
 *   Active rows, in order:
 *     1. Add Note
 *     2. Edit Appointment        — only if status != Fulfilled
 *     3. View Recurring Series   — only if recurringId present
 *     4. Cancel Appointment (red)— only if status not Fulfilled / NoShow
 *
 * Gates (Flutter): Edit/View → `Manage_Patient_Appointments` view OR the
 * current user is the appointment's provider. Cancel → same with edit.
 *
 * Edit / View / View-Recurring open the scheduling appointment panel via the
 * existing route `/scheduling/appointment/:id` (no cross-domain import — the
 * `patients/` domain may not import `scheduling/`). Add Note + Cancel reuse
 * the already-wired scheduling mutation hooks.
 */

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { RowActionsTrigger } from '../row-actions-trigger';
import { AnchoredPopover, AnchoredPopoverContent } from '@scripsteam/scrips-signal-ds';
import { Button, Modal, Select } from '@scripsteam/scrips-signal-ds';
import { TextArea as Textarea } from '@scripsteam/scrips-signal-ds';
import { APPT_GLYPH, ROW_ACTION_GLYPH, type ApptGlyph } from '@/scheduling/lib/appointment-glyphs';
import { useAddAppointmentNote } from '@/scheduling/services/use-add-appointment-note';
import { useCancelAppointment } from '@/scheduling/services/use-cancel-appointment';
import { useCancelRecurringAppointment } from '@/scheduling/services/use-cancel-recurring-appointment';
import { useUpdateAppointmentStatus } from '@/scheduling/services/use-update-appointment-status';
import {
  getRowActionsFor,
  statusCodeForAction,
  type AppointmentAction,
} from '@/scheduling/lib/appointment-actions';
import type {
  AppointmentCancellationReason,
  AppointmentStatus,
  ListAppointmentResponse,
} from '@/types/scheduling-api';
import { practiceNow } from '@/shared/lib/practice-time';

const STATUS_FULFILLED = 'Fulfilled';
const STATUS_NO_SHOW = 'NoShow';
const STATUS_CANCELLED = 'Cancelled';

export interface PatientAppointmentRowActionsProps {
  row: ListAppointmentResponse;
  /** `Manage_Patient_Appointments` view — gates Edit / View. */
  canView: boolean;
  /** `Manage_Patient_Appointments` edit — gates Cancel + status changes. */
  canEdit: boolean;
  currentUserId: string;
  cancellationReasons: AppointmentCancellationReason[];
  /** Full status master list — resolves the target AppointmentStatus for a
   *  quick status change (Mark Arrived / No Show / Confirm / Unmark …). */
  statusList: AppointmentStatus[];
  /** The `Cancelled` master status (statusList.find code === 'Cancelled'). */
  cancelledStatus?: AppointmentStatus;
  /** Refetch the list after a mutation. */
  onChanged: () => void;
}

/**
 * Timezone-anchored "is this appointment today?" gate (review: TZ-awareness).
 *
 * `start` arrives WITHOUT a `Z` suffix — the backend has already converted it
 * to the timezone we pass as `LoggedInUserTimezone` (the Intl-resolved browser
 * zone, see `use-patient-appointments.ts`). So the wire date prefix
 * (`YYYY-MM-DD`) is already expressed in that zone. We compare it against
 * today's date computed in the SAME zone (browser-local `Date`), via string
 * prefix — NOT `new Date(startISO)` reparse, which would re-interpret the
 * naive timestamp and could drift a day if the runtime zone differed. Both
 * sides are anchored to the one zone the times were rendered in, so a
 * UTC-server / GST-client split can't open or close the actions incorrectly.
 */
function isTodayISO(startISO?: string | null): boolean {
  if (!startISO) return false;
  const n = practiceNow();
  const p = (x: number) => String(x).padStart(2, '0');
  const todayYMD = `${n.getFullYear()}-${p(n.getMonth() + 1)}-${p(n.getDate())}`;
  return startISO.slice(0, 10) === todayYMD;
}

function findParticipant(row: ListAppointmentResponse, type: string) {
  return row.participant?.find(
    (p) =>
      typeof p.referenceType === 'string' &&
      p.referenceType.toLowerCase() === type,
  );
}

export function PatientAppointmentRowActions({
  row,
  canView,
  canEdit,
  currentUserId,
  cancellationReasons,
  statusList,
  cancelledStatus,
  onChanged,
}: PatientAppointmentRowActionsProps) {
  // PROD-2743 · switch ON: the DS `more` glyph, as the patients list row actions (#548). OFF = today.
  const navigate = useNavigate();
  const location = useLocation();
  const updateStatus = useUpdateAppointmentStatus();
  const [menuOpen, setMenuOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  // The tooltip names the row: its reason and date ("Actions for Follow-up, 12 Oct"), else just the date.
  const startDate = row.start ? new Date(row.start) : null;
  const apptWhen =
    startDate && !Number.isNaN(startDate.getTime())
      ? startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
      : '';
  const apptLabel =
    [row.reasonCode?.display, apptWhen].filter(Boolean).join(', ') ||
    'appointment';

  const statusCode = row.status?.code;
  const isInactive =
    statusCode === STATUS_CANCELLED || statusCode === STATUS_NO_SHOW;
  const providerId = findParticipant(row, 'provider')?.provider?.id;
  const isOwnProvider = !!providerId && providerId === currentUserId;
  const allowView = canView || isOwnProvider;
  const allowEdit = canEdit || isOwnProvider;

  const open = (fn: () => void) => () => {
    setMenuOpen(false);
    fn();
  };

  const goToPanel = () => {
    if (!allowView) {
      toast.error('You are not authorized to view this appointment');
      return;
    }
    // Pass the current URL as `from` so the panel's Back returns to this
    // patient page instead of the calendar.
    navigate(
      `/scheduling/appointment/${row.id}?from=${encodeURIComponent(location.pathname + location.search)}`,
    );
  };

  // Inline status-action icons — Flutter's `AppointmentStatus` widget (the
  // last table column). Same rule set as the calendar row
  // (`getRowActionsFor`), minus `overflow` (we already render a dedicated
  // kebab). Quick status changes are gated on edit-or-own, matching Flutter.
  const inlineActions: AppointmentAction[] = getRowActionsFor({
    statusCode: statusCode ?? '',
    isToday: isTodayISO(row.start),
    isHomeLocation: (row.location?.name ?? '').toLowerCase() === 'home',
    isRecurring: !!row.recurringId,
  }).filter((a) => a.id !== 'overflow');

  const runStatusChange = (target: string) => {
    if (!allowEdit) {
      toast.error('You are not authorized to update this appointment');
      return;
    }
    const status = statusList.find(
      (s) => (s.code ?? '').toLowerCase() === target.toLowerCase(),
    );
    if (!status || updateStatus.isPending) return;
    updateStatus.mutate(
      { appointmentId: row.id, status },
      { onSuccess: onChanged },
    );
  };

  const handleInline = (action: AppointmentAction) => () => {
    const code = statusCodeForAction(action.id);
    if (code) return runStatusChange(code);
    if (action.id === 'reschedule' || action.id === 'follow-up') {
      return goToPanel();
    }
    if (action.id === 'cancel') {
      if (!allowEdit) {
        toast.error('You are not authorized to cancel this appointment');
        return;
      }
      return setCancelOpen(true);
    }
    if (action.id === 'edit') return goToPanel();
  };

  return (
    <>
      <div className="flex items-center justify-end gap-1">
        {inlineActions.map((action) => (
          <button
            key={action.id}
            type="button"
            aria-label={action.label}
            disabled={updateStatus.isPending}
            onClick={handleInline(action)}
            // The app icon button, the calendar row's glyph for the action, neutral (icon pass, 16 Oct: the
            // tone colours were decoration on an idle control; the label names the action).
            className="icon-action disabled:opacity-50"
          >
            <InlineGlyph id={action.id} />
          </button>
        ))}
        <AnchoredPopover open={menuOpen} onOpenChange={setMenuOpen}>
        <RowActionsTrigger ariaLabel="Appointment actions" tooltip={`Actions for ${apptLabel}`}>
          <MoreHorizontal className="size-4" aria-hidden />
        </RowActionsTrigger>
        <AnchoredPopoverContent collisionAvoidance={{ fallbackAxisSide: 'end' }} align="end" side="bottom" className="w-52 p-1 gap-3">
          {isInactive ? (
            <MenuItem
              icon="appointment"
              label="View Appointment"
              onClick={open(goToPanel)}
            />
          ) : (
            <>
              <MenuItem
                icon="comment"
                label="Add Note"
                onClick={open(() => setNoteOpen(true))}
              />
              {statusCode !== STATUS_FULFILLED ? (
                <MenuItem
                  icon="edit"
                  label="Edit Appointment"
                  onClick={open(goToPanel)}
                />
              ) : null}
              {row.recurringId ? (
                <MenuItem
                  icon="recurring"
                  label="View Recurring Series"
                  onClick={open(goToPanel)}
                />
              ) : null}
              {statusCode !== STATUS_FULFILLED &&
              statusCode !== STATUS_NO_SHOW ? (
                <MenuItem
                  icon="cancelled"
                  label="Cancel Appointment"
                  destructive
                  onClick={open(() => {
                    if (!allowEdit) {
                      toast.error(
                        'You are not authorized to cancel this appointment',
                      );
                      return;
                    }
                    setCancelOpen(true);
                  })}
                />
              ) : null}
            </>
          )}
        </AnchoredPopoverContent>
      </AnchoredPopover>
      </div>

      <AddNoteDialog
        open={noteOpen}
        onOpenChange={setNoteOpen}
        row={row}
        onChanged={onChanged}
      />
      <CancelAppointmentDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        row={row}
        currentUserId={currentUserId}
        reasons={cancellationReasons}
        cancelledStatus={cancelledStatus}
        onChanged={onChanged}
      />
    </>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  destructive,
}: {
  icon: ApptGlyph;
  label: string;
  onClick: () => void;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 rounded px-3 py-1.5 text-left text-sm hover:bg-grey-100',
        destructive ? 'text-status-error' : 'text-text-primary',
      )}
    >
      <MenuGlyph icon={icon} />
      <span>{label}</span>
    </button>
  );
}

function AddNoteDialog({
  open,
  onOpenChange,
  row,
  onChanged,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  row: ListAppointmentResponse;
  onChanged: () => void;
}) {
  const [note, setNote] = useState(row.comment ?? '');
  const addNote = useAddAppointmentNote();

  // Re-seed the textarea each time the dialog opens (the component stays
  // mounted across rows, so initial state alone would stick to the first row).
  useEffect(() => {
    if (open) setNote(row.comment ?? '');
  }, [open, row.id, row.comment]);

  const submit = () => {
    addNote.mutate(
      { id: row.id, note },
      {
        onSuccess: () => {
          toast.success('Note added');
          onOpenChange(false);
          onChanged();
        },
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title="Add Internal Note"
      size="sm"
      footer={<><Button
            type="button"
            size="sm"
            onClick={submit}
            disabled={addNote.isPending}
          >
            Save
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button></>}
    >
        
        <Textarea
          autoFocus
          value={note}
          maxLength={300}
          rows={4}
          placeholder="Add internal note"
          onChange={(e) => setNote(e.target.value)}
        />
        
      </Modal>
  );
}

type RecurringScope = 'this' | 'all';

function CancelAppointmentDialog({
  open,
  onOpenChange,
  row,
  currentUserId,
  reasons,
  cancelledStatus,
  onChanged,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  row: ListAppointmentResponse;
  currentUserId: string;
  reasons: AppointmentCancellationReason[];
  cancelledStatus?: AppointmentStatus;
  onChanged: () => void;
}) {
  const [reasonCode, setReasonCode] = useState<string>('');
  const [scope, setScope] = useState<RecurringScope>('this');
  const cancelAppt = useCancelAppointment();
  const cancelRecurring = useCancelRecurringAppointment();
  const updateStatus = useUpdateAppointmentStatus();
  const queryClient = useQueryClient();

  const patient = findParticipant(row, 'patient')?.patient;
  const providerId = findParticipant(row, 'provider')?.provider?.id;
  const isRecurring = !!row.recurringId;
  const reason = reasons.find((r) => r.code === reasonCode);
  const pending =
    cancelAppt.isPending || cancelRecurring.isPending || updateStatus.isPending;

  const afterSuccess = () => {
    toast.success('Appointment cancelled successfully');
    onOpenChange(false);
    setReasonCode('');
    queryClient.invalidateQueries({ queryKey: ['patient-appointments'] });
    onChanged();
  };

  const submit = () => {
    if (!reason) return;
    if (isRecurring) {
      cancelRecurring.mutate(
        {
          recurringId: row.recurringId!,
          cancellationReason: reason,
          patientId: patient?.id,
          practitionerId: providerId,
          staffId: currentUserId,
          allAppointment: scope === 'all',
          appointmentDate: scope === 'this' ? row.start : undefined,
        },
        { onSuccess: afterSuccess },
      );
      return;
    }
    cancelAppt.mutate(
      {
        appointmentId: row.id,
        cancellationReason: reason,
        patientId: patient?.id,
        practitionerId: providerId,
        staffId: currentUserId,
      },
      {
        onSuccess: () => {
          // Flutter also fires UpdateStatusEvent → sets the row to Cancelled.
          if (cancelledStatus) {
            updateStatus.mutate(
              { appointmentId: row.id, status: cancelledStatus },
              { onSuccess: afterSuccess, onError: afterSuccess },
            );
          } else {
            afterSuccess();
          }
        },
      },
    );
  };

  return (
    // Plain `Modal`, not `ModalYesNo`: this dialog gates its confirm button on
    // form validity (`!reason`), independent of the mutation's `pending` state.
    // `ModalYesNo` only exposes `loading` (which would also disable Cancel and
    // show a spinner when nothing is loading) — no `disabled`/`confirmDisabled`
    // prop for a validation gate. Flagged as a DS gap in the handback.
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title="Cancel Appointment"
      size="sm"
      footer={
        <>
          <Button
            type="button"
            size="sm"
            onClick={submit}
            disabled={!reason || pending}
          >
            Submit
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
        </>
      }
    >
        <p className="text-sm text-text-secondary">
          When you cancel we will notify {patient?.firstName ?? 'the patient'}{' '}
          about this change using their preferred communication method.
        </p>

        {isRecurring ? (
          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="recurring-scope"
                checked={scope === 'this'}
                onChange={() => setScope('this')}
              />
              This appointment
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="recurring-scope"
                checked={scope === 'all'}
                onChange={() => setScope('all')}
              />
              Entire series
            </label>
          </div>
        ) : null}

        <Select
          value={reasonCode}
          onChange={(v) => setReasonCode(v ?? '')}
          placeholder="Select reason for cancellation"
          options={reasons.map((r) => ({ label: r.display, value: r.code }))}
        />
    </Modal>
  );
}

/** An inline row action's glyph: the calendar row's map (icon pass, 16 Oct), 16. */
function InlineGlyph({ id }: { id: keyof typeof ROW_ACTION_GLYPH }) {
  const Glyph = ROW_ACTION_GLYPH[id];
  return <Glyph className="size-4" aria-hidden />;
}

/** A menu item's glyph: the appointment glyph map (icon pass, 16 Oct), 16, in the item's ink. */
function MenuGlyph({ icon }: { icon: ApptGlyph }) {
  const Glyph = APPT_GLYPH[icon];
  return <Glyph className="size-4" aria-hidden />;
}
